import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';
import '../../style/dashboard.css';

const BASE_URL = process.env.REACT_APP_BASE_URL;



// --- Circular Progress Component ---
const CircularProgress = ({ percentage }) => {
    const sqSize = 150;
    const strokeWidth = 10;
    const radius = (sqSize - strokeWidth) / 2;
    const viewBox = `0 0 ${sqSize} ${sqSize}`;
    const dashArray = radius * Math.PI * 2;
    const dashOffset = dashArray - dashArray * percentage / 100;

    return (
        <svg width={sqSize} height={sqSize} viewBox={viewBox} className="circular-progress">
            <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#34d399" />
                    <stop offset="100%" stopColor="#0d9488" />
                </linearGradient>
            </defs>
            <circle
                className="circle-background"
                cx={sqSize / 2}
                cy={sqSize / 2}
                r={radius}
                strokeWidth={strokeWidth}
            />
            <circle
                className="circle-progress"
                cx={sqSize / 2}
                cy={sqSize / 2}
                r={radius}
                strokeWidth={strokeWidth}
                transform={`rotate(-90 ${sqSize / 2} ${sqSize / 2})`}
                style={{ strokeDasharray: dashArray, strokeDashoffset: dashOffset }}
            />
            <text className="circle-text" x="50%" y="50%" dy=".3em" textAnchor="middle">
                {percentage.toFixed(1)}%
            </text>
        </svg>
    );
};

const BulkMetaSlip = () => {
    const { appId, language } = useAppId();
    const navigate = useNavigate();
    
    const [slipData, setSlipData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Bulk Meta Slip Dashboard",
            boothNo: "Booth Number",
            total: "Total",
            sent: "Sent",
            loading: "Loading data...",
            noData: "No slip data available."
        },
        hi: {
            pageTitle: "बल्क मेटा स्लिप डैशबोर्ड",
            boothNo: "बूथ नंबर",
            total: "कुल",
            sent: "भेजा गया",
            loading: "डेटा लोड हो रहा है...",
            noData: "कोई डेटा उपलब्ध नहीं है।"
        },
        gu: {
            pageTitle: "બલ્ક મેટા સ્લિપ",
            boothNo: "બૂથ નંબર",
            total: "ટોટલ",
            sent: "મોકલેલ",
            loading: "માહિતી લોડ થઈ રહી છે...",
            noData: "કોઈ માહિતી ઉપલબ્ધ નથી."
        }
    };

    const txt = t[lang] || t.en;

    // --- Fetch Data ---
    useEffect(() => {
        if (!appId) {
            navigate("/");
            return;
        }

        const fetchSlipData = async () => {
            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_meta_slip_send_dash`, {
                    app_id: appId
                });

                if (response.data.success && response.data.data && response.data.data.result) {
                    setSlipData(response.data.data.result);
                } else {
                    setSlipData([]);
                }
            } catch (err) {
                console.error("Error fetching bulk meta slip data:", err);
                setError("Failed to fetch data.");
                setSlipData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchSlipData();
    }, [appId, navigate]);

    return (
        <div className="app-container p-4" style={{ backgroundColor: '#eef2f6', minHeight: '100vh' }}>
            <Header title={txt.pageTitle} />

            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loading}</p>
                </div>
            )}

            {!loading && slipData.length === 0 && (
                <div className="text-center text-muted p-5 modern-card mt-4">
                    <h4>{error || txt.noData}</h4>
                </div>
            )}

            {!loading && slipData.length > 0 && (
                <div className="row g-4 mt-2 pb-5">
                    {slipData.map((item, index) => {
                        // Calculate percentage securely
                        const total = item.total_voter || 0;
                        const send = item.send || 0;
                        const percentage = total > 0 ? (send / total) * 100 : 0;
                        const pending = Math.max(total - send, 0);

                        return (
                            <div className="col-6 col-md-4 col-xl-3 slide-up-element" key={index} style={{ animationDelay: `${index * 0.05}s` }} onClick={()=> {navigate('/send_booth_wise_bulk_slip',{state:{booth_no:item.booth_no}})}}>
                                <div className="slip-dash-card">

                                    {/* Top accent bar */}
                                    <div className="card-accent-bar"></div>

                                    {/* Booth Header - combined centered text */}
                                    <div className="slip-booth-header">
                                        <h3 className="slip-booth-title">
                                            {txt.boothNo} - {item.booth_no}
                                        </h3>
                                    </div>

                                    <div className="slip-progress-wrapper">
                                        <CircularProgress percentage={percentage} />
                                        {pending > 0 && (
                                            <span className="pending-tag">{pending} pending</span>
                                        )}
                                        {pending === 0 && total > 0 && (
                                            <span className="complete-tag">
                                                <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                Complete
                                            </span>
                                        )}
                                    </div>

                                    <div className="slip-stats-row">
                                        <div className="slip-stat-box box-total">
                                            
                                            <span className="stat-value">{item.total_voter}</span>
                                            <span className="stat-label">{txt.total}</span>
                                        </div>
                                        <div className="slip-stat-box box-sent">
                                            
                                            <span className="stat-value">{item.send}</span>
                                            <span className="stat-label">{txt.sent}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <style jsx>{`
                /* Container and Animations */
                .app-container {
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                }
                
                .slide-up-element { 
                    animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
                    opacity: 0;
                }
                
                @keyframes slideUp { 
                    from { opacity: 0; transform: translateY(20px); } 
                    to { opacity: 1; transform: translateY(0); } 
                }

                /* Loader Styles */
                .loader-wrapper {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    min-height: 50vh;
                    width: 100%;
                }
                .spinner {
                    width: 45px;
                    height: 45px;
                    border: 4px solid #e2e8f0;
                    border-top: 4px solid #2563eb;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-bottom: 16px;
                }
                .loader-text {
                    color: #64748b;
                    font-weight: 600;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                /* ============================================
                   PREMIUM MODERN CARD DESIGN (Compact + Bigger Chart)
                   ============================================ */
                .slip-dash-card {
                    position: relative;
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 12px 10px 10px 10px;
                    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.03);
                    border: 1px solid #f1f5f9;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease, border-color 0.25s ease;
                    height: 100%;
                    overflow: hidden;
                }

                .slip-dash-card:hover {
                    transform: translateY(-6px);
                    box-shadow: 0 16px 32px -8px rgba(15, 23, 42, 0.12);
                    border-color: #e2e8f0;
                }

                /* Top accent gradient bar */
                .card-accent-bar {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #34d399, #0d9488);
                    border-radius: 16px 16px 0 0;
                }

                /* Booth Header - Combined centered black text */
                .slip-booth-header {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    margin-bottom: 10px;
                    text-align: center;
                }

                .slip-booth-title {
                    font-size: 20px;
                    font-weight: 600;
                    color: #000000;
                    margin: 0;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                /* Circular Progress Styles - bigger chart within compact card */
                .slip-progress-wrapper {
                    margin-bottom: 10px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    gap: 6px;
                }

                .circle-background {
                    fill: none;
                    stroke: #f1f5f9;
                }

                .circle-progress {
                    fill: none;
                    stroke: url(#progressGradient);
                    stroke-linecap: round;
                    transition: stroke-dashoffset 1.1s cubic-bezier(0.16, 1, 0.3, 1);
                    filter: drop-shadow(0 2px 4px rgba(13, 148, 136, 0.25));
                }

                .circle-text {
                    fill: #0f172a;
                    font-size: 19px;
                    font-weight: 800;
                    font-family: inherit;
                }

                .pending-tag {
                    font-size: 10px;
                    font-weight: 700;
                    color: #b45309;
                    background: #fffbeb;
                    border: 1px solid #fde68a;
                    padding: 2px 8px;
                    border-radius: 20px;
                }

                .complete-tag {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    font-size: 10px;
                    font-weight: 700;
                    color: #15803d;
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    padding: 2px 8px;
                    border-radius: 20px;
                }

                /* Bottom Stats Row */
                .slip-stats-row {
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                    gap: 6px;
                    margin-top: auto;
                }

                .slip-stat-box {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 8px 4px 6px 4px;
                    border-radius: 10px;
                    position: relative;
                    transition: transform 0.2s ease;
                }

                .slip-stat-box:hover {
                    transform: translateY(-2px);
                }

                .stat-icon-wrap {
                    width: 20px;
                    height: 20px;
                    border-radius: 7px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 4px;
                }

                .total-icon {
                    background: rgba(59, 47, 123, 0.1);
                    color: #3b2f7b;
                }

                .sent-icon {
                    background: rgba(34, 197, 94, 0.12);
                    color: #16a34a;
                }

                .stat-label {
                    font-size: 9.5px;
                    font-weight: 700;
                    color: #64748b;
                    margin-top: 1px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .stat-value {
                    font-size: 26px; /* increased from 16px */
                    font-weight: 800;
                    line-height: 1.1;
                }

                /* Color Coding for Boxes */
                .box-total {
                    background: linear-gradient(160deg, #f5f3ff 0%, #ede9fe 100%);
                    border: 1px solid #ddd6fe;
                }
                .box-total .stat-value {
                    color: #3b2f7b;
                }

                .box-sent {
                    background: linear-gradient(160deg, #f0fdf4 0%, #dcfce7 100%);
                    border: 1px solid #bbf7d0;
                }
                .box-sent .stat-value {
                    color: #16a34a;
                }

                /* Responsive Adjustments */
                @media (max-width: 480px) {
                    .slip-dash-card {
                        padding: 10px 8px 8px 8px;
                        border-radius: 14px;
                    }
                    .slip-booth-header {
                        margin-bottom: 8px;
                    }
                    .slip-booth-title {
                        font-size: 17px;
                    }
                    .slip-stats-row {
                        gap: 5px;
                    }
                    .stat-value {
                        font-size: 22px; /* increased from 14px */
                    }
                    .stat-label {
                        font-size: 8.5px;
                    }
                    .slip-progress-wrapper {
                        margin-bottom: 8px;
                    }
                    .circular-progress {
                        width: 130px;
                        height: 130px;
                    }
                    .circle-text {
                        font-size: 17px;
                    }
                }
            `}</style>
        </div>
    );
};

export default BulkMetaSlip;